import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  applyScore,
  averageFor,
  endInning,
  formatClock,
  gamePreset,
  holdShotClock,
  label,
  pressureState,
  recordFoul,
  restartShotClock,
  TABS,
  timerDurationFor,
  undoRace,
  usesInnings,
} from './raceRoomEngine';
import {
  createRace,
  listenRaces,
  loadPool4RealProfile,
  patchRace,
  removeRace,
  saveRace,
  searchPool4RealPlayers,
} from './raceRoomService';
import './AndroidRaceRoom.css';

const n = value => Math.max(0, Number(value || 0));
const suffix = side => side === 'PLAYER_A' ? 'A' : 'B';
const playerFor = (race, side) => side === 'PLAYER_A'
  ? (race.playerA || { name: race.playerAName })
  : (race.playerB || { name: race.playerBName });
const targetFor = (race, side) => Math.max(1, Number(race?.[`raceTo${suffix(side)}`] || race?.raceTo || 5));
const isCompleted = race => race?.status === 'COMPLETED';

function useWakeLock(enabled) {
  const wakeLock = useRef(null);
  useEffect(() => {
    let cancelled = false;
    const acquire = async () => {
      if (!enabled || !window.navigator.wakeLock?.request || document.visibilityState !== 'visible') return;
      try {
        wakeLock.current = await window.navigator.wakeLock.request('screen');
        if (cancelled && wakeLock.current) await wakeLock.current.release();
      } catch (_) { /* optional browser capability */ }
    };
    const onVisibility = () => { if (document.visibilityState === 'visible') acquire(); };
    acquire();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      wakeLock.current?.release?.().catch(() => {});
      wakeLock.current = null;
    };
  }, [enabled]);
}

export default function AndroidRaceRoom({ user, profile }) {
  const [races, setRaces] = useState([]);
  const [activeId, setActiveId] = useState('');
  const [tab, setTab] = useState('PLAY');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [tick, setTick] = useState(Date.now());
  const [dbProfile, setDbProfile] = useState(profile || null);
  const [fullscreenDisplay, setFullscreenDisplay] = useState(false);
  const [showTvControls, setShowTvControls] = useState(false);
  const shellRef = useRef(null);

  const active = useMemo(() => races.find(race => race.id === activeId) || null, [races, activeId]);
  const liveDisplay = fullscreenDisplay && Boolean(active);

  useWakeLock(Boolean(liveDisplay && active?.display?.keepScreenAwake !== false));

  useEffect(() => {
    let live = true;
    if (user) loadPool4RealProfile(user.uid).then(next => live && setDbProfile(next)).catch(() => {});
    return () => { live = false; };
  }, [user]);

  useEffect(() => {
    if (!user) return undefined;
    return listenRaces(
      user.uid,
      rows => {
        setRaces(rows);
        setActiveId(id => {
          const current = rows.find(row => row.id === id);
          if (current && current.status !== 'COMPLETED' && current.status !== 'CANCELLED') return id;
          const openRace = rows.find(row => row.status !== 'COMPLETED' && row.status !== 'CANCELLED');
          return openRace?.id || '';
        });
      },
      nextError => setError(nextError.message || 'Unable to load Race Room sessions.'),
    );
  }, [user]);

  useEffect(() => {
    const id = window.setInterval(() => setTick(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    const onFullscreenChange = () => {
      if (!document.fullscreenElement && fullscreenDisplay) setFullscreenDisplay(false);
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, [fullscreenDisplay]);

  useEffect(() => {
    if (!liveDisplay) return undefined;
    const previousOverflow = document.body.style.overflow;
    const previousOverscroll = document.body.style.overscrollBehavior;
    document.body.style.overflow = 'hidden';
    document.body.style.overscrollBehavior = 'none';
    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.style.overscrollBehavior = previousOverscroll;
    };
  }, [liveDisplay]);

  useEffect(() => {
    if (!liveDisplay || !active?.display?.lockLandscape || !window.screen?.orientation?.lock) return undefined;
    window.screen.orientation.lock('landscape').catch(() => {});
    return () => window.screen?.orientation?.unlock?.();
  }, [liveDisplay, active?.display?.lockLandscape]);

  const matchSeconds = active
    ? n(active.flow?.matchElapsedSeconds) + (active.flow?.matchClockRunning && active.flow?.matchClockStartedAtMs
      ? Math.floor((tick - active.flow.matchClockStartedAtMs) / 1000)
      : 0)
    : 0;
  const timerSeconds = active
    ? Math.max(0, n(active.flow?.timerRemainingSeconds) - (active.flow?.timerRunning && active.flow?.timerStartedAtMs
      ? Math.floor((tick - active.flow.timerStartedAtMs) / 1000)
      : 0))
    : 0;

  useEffect(() => {
    if (!active?.flow?.timerRunning || active.isPaused || timerSeconds > 0) return;
    patchRace(active.id, { flow: holdShotClock(active.flow, 0) }).catch(() => {});
  }, [active?.id, active?.flow, active?.isPaused, timerSeconds]);

  const commit = async next => {
    if (!active) return;
    setBusy('save');
    try { await saveRace(active.id, next); }
    catch (nextError) { setError(nextError.message || 'Unable to save Race Room.'); }
    finally { setBusy(''); }
  };

  const update = (key, value) => commit({ ...active, [key]: value });
  const create = async () => {
    if (!user) { setError('Sign in to start a Race Room.'); return; }
    setBusy('create');
    try {
      const id = await createRace(user, dbProfile);
      setActiveId(id);
      setTab('PLAY');
      setFullscreenDisplay(false);
    } catch (nextError) { setError(nextError.message || 'Unable to create Race Room.'); }
    finally { setBusy(''); }
  };

  const canScore = () => active && user && !(active.syncMode === 'HOST_CONTROL' && active.createdBy !== user.uid);
  const addScore = async side => {
    if (!active || isCompleted(active) || !canScore()) return;
    if (active.syncMode === 'CONFIRM_SCORES' && active.createdBy !== user.uid) {
      await patchRace(active.id, {
        pendingScoreAction: {
          actionId: window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`, 
          createdByUid: user.uid,
          createdByName: dbProfile?.nickname || dbProfile?.name || 'Pool Player',
          requiresConfirmationFromUid: active.createdBy,
          side,
          delta: 1,
          createdAtMillis: Date.now(),
        },
      });
      return;
    }
    await commit(applyScore(active, side, 1));
  };

  const startTiming = flow => ({
    ...restartShotClock(flow, true),
    matchClockRunning: true,
    matchClockStartedAtMs: flow?.matchClockRunning ? flow.matchClockStartedAtMs : Date.now(),
  });

  const selectSide = async side => {
    if (!active || isCompleted(active)) return;
    const previous = active.activeSide;
    if (previous === side) { await addScore(side); return; }
    if (usesInnings(active) && previous && previous !== 'NONE') {
      await commit(endInning(active, previous));
      return;
    }
    await commit({ ...active, activeSide: side, status: 'LIVE', isPaused: false, flow: startTiming(active.flow) });
  };

  const subtract = side => active && canScore() && commit(applyScore(active, side, -1));
  const miss = () => active && active.activeSide !== 'NONE' && commit(endInning(active, active.activeSide));
  const foul = () => active && active.activeSide !== 'NONE' && commit(recordFoul(active, active.activeSide));
  const resetShotClock = () => active && commit({ ...active, flow: restartShotClock(active.flow, !active.isPaused) });
  const togglePause = () => {
    if (!active) return;
    if (active.isPaused) {
      commit({ ...active, isPaused: false, flow: { ...startTiming(active.flow), matchClockStartedAtMs: Date.now() } });
    } else {
      commit({
        ...active,
        isPaused: true,
        flow: {
          ...holdShotClock(active.flow, timerSeconds),
          matchElapsedSeconds: matchSeconds,
          matchClockRunning: false,
          matchClockStartedAtMs: null,
        },
      });
    }
  };

  const startMatchClock = () => active && commit({ ...active, flow: { ...active.flow, matchClockRunning: true, matchClockStartedAtMs: Date.now() } });
  const pauseMatchClock = () => active && commit({ ...active, flow: { ...active.flow, matchElapsedSeconds: matchSeconds, matchClockRunning: false, matchClockStartedAtMs: null } });
  const resetMatchClock = () => active && commit({ ...active, flow: { ...active.flow, matchElapsedSeconds: 0, matchClockRunning: false, matchClockStartedAtMs: null } });
  const startTimer = () => active && commit({ ...active, flow: restartShotClock(active.flow, true) });
  const pauseTimer = () => active && commit({ ...active, flow: holdShotClock(active.flow, timerSeconds) });

  const enterBrowserFullscreen = async () => {
    const element = shellRef.current;
    if (!element || document.fullscreenElement) return;
    try { await element.requestFullscreen?.({ navigationUI: 'hide' }); }
    catch (_) { /* CSS fullscreen remains active when API is unavailable */ }
  };

  const openLiveDisplay = async () => {
    if (!active) return;
    enterBrowserFullscreen();
    setFullscreenDisplay(true);
    setShowTvControls(false);
  };

  const closeLiveDisplay = async () => {
    setFullscreenDisplay(false);
    setShowTvControls(false);
    if (document.fullscreenElement) {
      try { await document.exitFullscreen(); } catch (_) { /* no-op */ }
    }
  };

  const finishRace = () => {
    if (!active || !window.confirm('Finish this race and save the result?')) return;
    const winnerSide = n(active.scoreA) === n(active.scoreB) ? 'NONE' : n(active.scoreA) > n(active.scoreB) ? 'PLAYER_A' : 'PLAYER_B';
    commit({
      ...active,
      status: 'COMPLETED',
      winnerSide,
      winnerName: winnerSide === 'PLAYER_A' ? active.playerAName : winnerSide === 'PLAYER_B' ? active.playerBName : 'Tie',
      flow: { ...active.flow, matchElapsedSeconds: matchSeconds, matchClockRunning: false, matchClockStartedAtMs: null, timerRunning: false, timerStartedAtMs: null },
    });
  };

  const restartSame = () => active && commit({
    ...active,
    status: 'LIVE', scoreA: 0, scoreB: 0, inningsA: 0, inningsB: 0, currentRunA: 0, currentRunB: 0,
    highRunA: 0, highRunB: 0, activeSide: 'NONE', winnerSide: null, winnerName: null, pendingScoreAction: null,
    events: [], undoStack: [],
    flow: { ...active.flow, matchElapsedSeconds: 0, matchClockRunning: false, matchClockStartedAtMs: null, timerRunning: false, timerStartedAtMs: null, timerRemainingSeconds: active.flow?.timerDurationSeconds || 0 },
  });

  const differentGame = async () => { await restartSame(); setTab('FLOW'); setFullscreenDisplay(false); };

  if (liveDisplay) {
    return (
      <section ref={shellRef} className="arr-app arr-app-live" aria-label="Race Room live view">
        <LiveDisplay
          race={active}
          matchSeconds={matchSeconds}
          timerSeconds={timerSeconds}
          controlsOpen={showTvControls}
          onToggleControls={() => setShowTvControls(value => !value)}
          onExit={closeLiveDisplay}
          onTap={selectSide}
          onSubtract={subtract}
          onUndo={() => commit(undoRace(active))}
          onMiss={miss}
          onResetTimer={resetShotClock}
          onPauseRace={togglePause}
          onStartTimer={startTimer}
          onPauseTimer={pauseTimer}
          onStartMatchClock={startMatchClock}
          onPauseMatchClock={pauseMatchClock}
          onResetMatchClock={resetMatchClock}
          onFlip={() => commit({ ...active, display: { ...active.display, flipHorizontal: !active.display?.flipHorizontal } })}
          onLandscape={() => commit({ ...active, display: { ...active.display, lockLandscape: !active.display?.lockLandscape } })}
        />
        {isCompleted(active) && <WinnerPanel race={active} onRestart={restartSame} onDifferent={differentGame} onNew={create} onView={() => {}} compact />}
      </section>
    );
  }

  return (
    <section ref={shellRef} className="arr-app">
      <header className="arr-topbar">
        <div className="arr-topbar-title">
          <strong>Race Room</strong>
          <small>{active ? `${active.playerAName || 'Player 1'} vs ${active.playerBName || 'Player 2'}` : 'Quick match counter'}</small>
        </div>
        <div className="arr-topbar-actions">
          {busy === 'save' && <span className="arr-saving">Saving…</span>}
          <button type="button" className="arr-primary" onClick={create} disabled={busy === 'create'}>{busy === 'create' ? 'Starting…' : 'New race'}</button>
        </div>
      </header>

      <div className="arr-content">
        {error && <div className="arr-notice arr-notice-error"><span>{error}</span><button type="button" onClick={() => setError('')} aria-label="Dismiss">×</button></div>}
        <nav className="arr-tabs" aria-label="Race Room sections">
          {TABS.map(item => (
            <button type="button" className={tab === item ? 'active' : ''} onClick={() => setTab(item)} key={item}>{item === 'ROOM' ? 'Live View' : label(item)}</button>
          ))}
        </nav>

        {tab === 'SESSIONS' && <SessionList races={races.filter(race => race.status !== 'COMPLETED' && race.status !== 'CANCELLED')} activeId={activeId} setActiveId={setActiveId} setTab={setTab} />}
        {tab === 'HISTORY' && <SessionList races={races.filter(race => race.status === 'COMPLETED')} activeId={activeId} setActiveId={setActiveId} setTab={setTab} history />}
        {tab === 'PLAYERS' && (active ? <PlayersPanel race={active} user={user} commit={commit} /> : <EmptyRace onCreate={create} />)}
        {tab === 'FLOW' && (active ? <FlowPanel race={active} matchSeconds={matchSeconds} timerSeconds={timerSeconds} commit={commit} onPause={togglePause} onStartTimer={startTimer} onPauseTimer={pauseTimer} onResetTimer={resetShotClock} onStartMatchClock={startMatchClock} onPauseMatchClock={pauseMatchClock} onResetMatchClock={resetMatchClock} /> : <EmptyRace onCreate={create} />)}
        {tab === 'ROOM' && (active ? <DisplayPreview race={active} matchSeconds={matchSeconds} timerSeconds={timerSeconds} onOpen={openLiveDisplay} /> : <EmptyRace onCreate={create} />)}
        {tab === 'PLAY' && (active ? (
          <PlayTab
            race={active}
            matchSeconds={matchSeconds}
            timerSeconds={timerSeconds}
            onTap={selectSide}
            onSubtract={subtract}
            onMiss={miss}
            onFoul={foul}
            onResetTimer={resetShotClock}
            onUndo={() => commit(undoRace(active))}
            onPause={togglePause}
            onPlayers={() => setTab('PLAYERS')}
            onFlow={() => setTab('FLOW')}
            onLive={openLiveDisplay}
            onFinish={finishRace}
          />
        ) : <EmptyRace onCreate={create} />)}
      </div>

      {isCompleted(active) && <WinnerPanel race={active} onRestart={restartSame} onDifferent={differentGame} onNew={create} onView={openLiveDisplay} />}
    </section>
  );
}

function EmptyRace({ onCreate }) {
  return <div className="arr-empty"><div className="arr-empty-icon">8</div><h2>No active Race Room</h2><p>Start a quick race and the session will autosave while you play.</p><button type="button" className="arr-primary" onClick={onCreate}>Start race</button></div>;
}

function Collapsible({ title, subtitle, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return <section className="arr-panel arr-collapse"><button type="button" className="arr-collapse-head" onClick={() => setOpen(value => !value)}><span><strong>{title}</strong>{subtitle && <small>{subtitle}</small>}</span><b>{open ? 'Hide' : 'Show'}</b></button>{open && <div className="arr-collapse-body">{children}</div>}</section>;
}

function PlayTab({ race, matchSeconds, timerSeconds, onTap, onSubtract, onMiss, onFoul, onResetTimer, onUndo, onPause, onPlayers, onFlow, onLive, onFinish }) {
  const sessionSummary = `${label(race.status || 'LIVE')} • ${label(race.cueMode)} • ${race.scoreA || 0}–${race.scoreB || 0}`;
  return <div className="arr-stack">
    <Collapsible title="Current session" subtitle={sessionSummary}><SessionStatus race={race} matchSeconds={matchSeconds} timerSeconds={timerSeconds} /></Collapsible>
    <Collapsible title="Match flow" subtitle="Timers and table behavior."><div className="arr-quick-actions"><button type="button" onClick={onPause}>{race.isPaused ? 'Resume race' : 'Pause race'}</button><button type="button" onClick={onFlow}>Open Flow</button></div></Collapsible>
    <Collapsible title="Quick setup" subtitle={`${label(race.cueMode)} • ${label(race.scoringStyle)} • Race to ${targetFor(race, 'PLAYER_A')}`}><p className="arr-muted">Use Flow for game mode, score format, match targets, timer, synchronization, and display settings.</p><button type="button" onClick={onFlow}>Open quick setup</button></Collapsible>
    <Scoreboard race={race} matchSeconds={matchSeconds} timerSeconds={timerSeconds} onTap={onTap} onSubtract={onSubtract} onMiss={onMiss} onFoul={onFoul} onResetTimer={onResetTimer} />
    <div className="arr-control-dock">
      <button type="button" onClick={onUndo} disabled={!race.undoStack?.length}>Undo</button>
      <button type="button" onClick={onPause}>{race.isPaused ? 'Resume' : 'Pause'}</button>
      <button type="button" onClick={onPlayers}>Players</button>
      <button type="button" onClick={onLive} className="arr-primary">Live View</button>
      <button type="button" className="arr-danger" onClick={onFinish}>Finish</button>
    </div>
    <RecentEvents race={race} />
  </div>;
}

function SessionStatus({ race, matchSeconds, timerSeconds }) {
  return <div className="arr-status-grid"><span><small>Match</small><strong>{formatClock(matchSeconds)}</strong></span><span><small>Game</small><strong>{label(race.cueMode)}</strong></span><span><small>Shot timer</small><strong>{race.flow?.timerStyle === 'OFF' ? 'Off' : `${timerSeconds}s`}</strong></span><span><small>Pressure</small><strong>{label(pressureState(race))}</strong></span></div>;
}

function Scoreboard({ race, matchSeconds, timerSeconds, onTap, onSubtract, onMiss, onFoul, onResetTimer }) {
  const inningMode = usesInnings(race);
  const expired = race.flow?.timerStyle !== 'OFF' && timerSeconds === 0;
  return <section className={`arr-scoreboard ${race.isPaused ? 'is-paused' : ''} ${expired ? 'timer-expired' : ''}`}>
    <header className="arr-scoreboard-header"><span>{formatClock(matchSeconds)}</span><div><strong>{label(race.cueMode)}</strong><small>{label(race.scoringStyle)}</small></div><span>{race.flow?.timerStyle === 'OFF' ? 'Timer off' : expired ? 'TIME' : `${timerSeconds}s`}</span></header>
    <div className="arr-scoreboard-grid"><PlayerCard race={race} side="PLAYER_A" onTap={onTap} onSubtract={onSubtract} /><div className="arr-score-vs"><span>VS</span><small>Race to {targetFor(race, 'PLAYER_A')}</small></div><PlayerCard race={race} side="PLAYER_B" onTap={onTap} onSubtract={onSubtract} /></div>
    {inningMode && <div className="arr-inning-controls"><button type="button" className="arr-primary" onClick={onMiss} disabled={race.activeSide === 'NONE'}>End inning</button><button type="button" onClick={onFoul} disabled={race.activeSide === 'NONE'}>Foul</button><button type="button" onClick={onResetTimer} disabled={race.flow?.timerStyle === 'OFF'}>Reset shot clock</button></div>}
    <p className="arr-score-help">Tap a player to put them on the table. Tap the highlighted player again after a made shot to score.</p>
  </section>;
}

function PlayerCard({ race, side, onTap, onSubtract, live = false }) {
  const player = playerFor(race, side);
  const s = suffix(side);
  const active = race.activeSide === side;
  const score = n(race[`score${s}`]);
  const target = targetFor(race, side);
  const leading = n(race.scoreA) !== n(race.scoreB) && score > n(race[`score${s === 'A' ? 'B' : 'A'}`]);
  const accent = race.display?.[`player${s}Color`] || (s === 'A' ? '#8b5cf6' : '#d0bcff');
  const progress = Math.min(100, Math.round((score / target) * 100));
  return <article
    className={`arr-player-card ${active ? 'is-active' : ''} ${leading ? 'is-leading' : ''} ${live ? 'is-live' : ''}`}
    style={{ '--player-accent': accent }}
    onClick={() => onTap(side)}
    role="button"
    tabIndex={0}
    onKeyDown={event => (event.key === 'Enter' || event.key === ' ') && onTap(side)}
  >
    <span className="arr-player-state">{active ? 'ON TABLE' : leading ? 'LEADING' : 'READY'}</span>
    {race.display?.privacy !== 'SCORE_ONLY' && race.display?.showPhotos !== false && (player.photoUrl
      ? <img className="arr-player-photo" src={player.photoUrl} alt="" />
      : <div className="arr-player-fallback">{String(player.name || `P${s}`).slice(0, 1)}</div>)}
    <small className="arr-player-side">Player {s === 'A' ? '1' : '2'}</small>
    <h3>{race.display?.privacy === 'SCORE_ONLY' ? `Player ${s === 'A' ? '1' : '2'}` : (player.name || race[`player${s}Name`] || `Player ${s}`)}</h3>
    <strong className="arr-score-number">{score}</strong>
    <span className="arr-score-label">{usesInnings(race) ? `${n(race[`innings${s}`])} innings • ${averageFor(race, side).toFixed(3)} avg` : `Race to ${target} • ${progress}%`}</span>
    <div className="arr-player-stats"><span>Run <b>{n(race[`currentRun${s}`])}</b></span><span>High <b>{n(race[`highRun${s}`])}</b></span><span>Wins <b>{n(race[`raceWins${s}`])}</b></span></div>
    <button type="button" className="arr-remove" onClick={event => { event.stopPropagation(); onSubtract(side); }}>− Remove</button>
    <small className="arr-player-hint">{active ? 'Tap again to score' : 'Tap to put on table'}</small>
  </article>;
}

function PlayersPanel({ race, user, commit }) {
  const [queryText, setQueryText] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const timer = useRef();
  useEffect(() => () => clearTimeout(timer.current), []);
  const search = value => {
    setQueryText(value);
    clearTimeout(timer.current);
    if (value.trim().length < 2) { setRows([]); return; }
    timer.current = setTimeout(async () => {
      setLoading(true);
      try { setRows(await searchPool4RealPlayers(user.uid, value)); }
      finally { setLoading(false); }
    }, 250);
  };
  const choose = (side, player) => {
    const s = suffix(side);
    commit({
      ...race,
      [`player${s}`]: player,
      [`player${s}Name`]: player.name,
      [`player${s}Uid`]: player.uid || '',
      participantUids: Array.from(new Set([...(race.participantUids || []), player.uid].filter(Boolean))),
      acceptedUids: Array.from(new Set([...(race.acceptedUids || []), player.uid].filter(Boolean))),
    });
    setQueryText(''); setRows([]);
  };
  return <div className="arr-stack"><section className="arr-panel"><h2>Players</h2><p className="arr-muted">Search Pool 4 Real profiles or keep a manual guest name.</p><input value={queryText} onChange={event => search(event.target.value)} placeholder="Search a player nickname" />{loading && <small>Searching…</small>}<div className="arr-player-results">{rows.map(player => <article key={player.uid}>{player.photoUrl ? <img src={player.photoUrl} alt="" /> : <span>{player.name.slice(0, 1)}</span>}<div><strong>{player.name}</strong><small>{player.subtitle || 'Pool 4 Real player'}</small></div><button type="button" onClick={() => choose('PLAYER_A', player)}>Player 1</button><button type="button" onClick={() => choose('PLAYER_B', player)}>Player 2</button></article>)}</div></section><div className="arr-two-col"><ManualPlayer race={race} side="A" commit={commit} /><ManualPlayer race={race} side="B" commit={commit} /></div></div>;
}

function ManualPlayer({ race, side, commit }) {
  const key = `player${side}Name`;
  return <section className="arr-panel"><h3>Player {side === 'A' ? '1' : '2'}</h3><input value={race[key] || ''} onChange={event => commit({ ...race, [key]: event.target.value, [`player${side}`]: { ...(race[`player${side}`] || {}), name: event.target.value, isGuest: true } })} /><small className="arr-muted">Manual names remain available for guests.</small></section>;
}

function FlowPanel({ race, matchSeconds, timerSeconds, commit, onPause, onStartTimer, onPauseTimer, onResetTimer, onStartMatchClock, onPauseMatchClock, onResetMatchClock }) {
  const display = race.display || {};
  const flow = race.flow || {};
  const set = (key, value) => commit({ ...race, [key]: value });
  const setFlow = (key, value) => commit({ ...race, flow: { ...flow, [key]: value } });
  const setDisplay = (key, value) => commit({ ...race, display: { ...display, [key]: value } });
  return <div className="arr-stack">
    <section className="arr-panel"><h2>Flow</h2><p className="arr-muted">Timers and table behavior.</p><div className="arr-flow-clocks"><div><small>Match clock</small><strong>{formatClock(matchSeconds)}</strong><div><button type="button" onClick={flow.matchClockRunning ? onPauseMatchClock : onStartMatchClock}>{flow.matchClockRunning ? 'Pause' : 'Start'}</button><button type="button" onClick={onResetMatchClock}>Reset</button></div></div><div><small>Shot timer</small><strong>{flow.timerStyle === 'OFF' ? 'OFF' : `${timerSeconds}s`}</strong><div><button type="button" disabled={flow.timerStyle === 'OFF'} onClick={flow.timerRunning ? onPauseTimer : onStartTimer}>{flow.timerRunning ? 'Pause' : 'Start'}</button><button type="button" disabled={flow.timerStyle === 'OFF'} onClick={onResetTimer}>Reset</button></div></div></div><button type="button" onClick={onPause}>{race.isPaused ? 'Resume race' : 'Pause race'}</button></section>
    <section className="arr-panel"><h2>Quick setup</h2><div className="arr-form-grid">
      <label>Game mode<select value={race.cueMode || 'CLASSIC'} onChange={event => commit({ ...race, ...gamePreset(event.target.value), activeSide: 'NONE' })}><option value="CLASSIC">Classic</option><option value="EIGHT_BALL">8-Ball</option><option value="NINE_BALL">9-Ball</option><option value="TEN_BALL">10-Ball</option><option value="THREE_CUSHION">3-Cushion</option><option value="CUSTOM">Custom</option></select></label>
      <label>Session mode<select value={race.mode || 'CASUAL_COUNTER'} onChange={event => set('mode', event.target.value)}><option value="CASUAL_COUNTER">Casual Counter</option><option value="FOCUSED_RACE">Focused Race</option><option value="PRACTICE_SET">Practice Set</option><option value="FRIEND_MATCH">Friend Match</option></select></label>
      <label>Score format<select value={race.scoreMode || 'RACE_TO_TARGET'} onChange={event => set('scoreMode', event.target.value)}><option value="RACE_TO_TARGET">Race to target</option><option value="OPEN_RACK_COUNTER">Rack counter</option><option value="PRACTICE_RACKS">Practice racks</option></select></label>
      <label>Scoring style<select value={usesInnings(race) ? 'INNINGS' : (race.scoringStyle === 'POINTS' ? 'POINTS_RACE' : race.scoringStyle || 'RACKS')} onChange={event => set('scoringStyle', event.target.value)}><option value="RACKS">Racks</option><option value="POINTS_RACE">Points</option><option value="INNINGS">Points + innings</option></select></label>
      <label>Player 1 target<input type="number" min="1" max="999" value={targetFor(race, 'PLAYER_A')} onChange={event => set('raceToA', n(event.target.value))} /></label>
      <label>Player 2 target<input type="number" min="1" max="999" value={targetFor(race, 'PLAYER_B')} onChange={event => set('raceToB', n(event.target.value))} /></label>
      <label>After a rack/point<select value={race.tableTurnRule || 'WINNER_STAYS'} onChange={event => set('tableTurnRule', event.target.value)}><option value="WINNER_STAYS">Winner keeps table</option><option value="LOSER_BREAKS">Other player gets table</option><option value="MANUAL">Manual table control</option></select></label>
      <label>Synchronization<select value={race.syncMode || 'LOCAL_ONLY'} onChange={event => set('syncMode', event.target.value)}><option value="LOCAL_ONLY">Local only</option><option value="HOST_CONTROL">Host controls</option><option value="SHARED_CONTROL">Shared control</option><option value="CONFIRM_SCORES">Confirm scores</option></select></label>
      <label>Shot timer<select value={flow.timerStyle || 'OFF'} onChange={event => { const timerStyle = event.target.value; const duration = timerDurationFor({ ...flow, timerStyle }); commit({ ...race, flow: { ...flow, timerStyle, timerDurationSeconds: duration, timerRemainingSeconds: duration, timerRunning: false, timerStartedAtMs: null } }); }}><option value="OFF">Off</option><option value="GENTLE_RHYTHM">Gentle rhythm · 60s</option><option value="FOCUS_TIMER">Focus · 45s</option><option value="PRESSURE_TIMER">Pressure · 30s</option></select></label>
      <label className="arr-check"><input type="checkbox" checked={flow.autoRestartShotTimer !== false} onChange={event => setFlow('autoRestartShotTimer', event.target.checked)} />Auto restart shot timer</label>
    </div></section>
    <section className="arr-panel"><h2>Live View</h2><div className="arr-form-grid"><label>Privacy<select value={display.privacy || 'FULL_PROFILE'} onChange={event => setDisplay('privacy', event.target.value)}><option value="FULL_PROFILE">Full player cards</option><option value="MATCH_ONLY">Match info only</option><option value="SCORE_ONLY">Score only</option></select></label><label className="arr-check"><input type="checkbox" checked={display.showPhotos !== false} onChange={event => setDisplay('showPhotos', event.target.checked)} />Show photos</label><label className="arr-check"><input type="checkbox" checked={display.flipHorizontal === true} onChange={event => setDisplay('flipHorizontal', event.target.checked)} />Swap player sides</label><label className="arr-check"><input type="checkbox" checked={display.largeTextMode !== false} onChange={event => setDisplay('largeTextMode', event.target.checked)} />Large text</label><label className="arr-check"><input type="checkbox" checked={display.lockLandscape === true} onChange={event => setDisplay('lockLandscape', event.target.checked)} />Lock landscape when supported</label><label className="arr-check"><input type="checkbox" checked={display.keepScreenAwake !== false} onChange={event => setDisplay('keepScreenAwake', event.target.checked)} />Keep screen awake</label></div></section>
  </div>;
}

function DisplayPreview({ race, matchSeconds, timerSeconds, onOpen }) {
  return <div className="arr-stack"><section className="arr-panel arr-live-intro"><div><h2>Live View</h2><p>Full-screen table display with the same scoring controls as Android.</p></div><button type="button" className="arr-primary" onClick={onOpen}>Open Live View</button></section><div className="arr-preview"><LiveDisplay race={race} matchSeconds={matchSeconds} timerSeconds={timerSeconds} preview onTap={() => {}} onSubtract={() => {}} onToggleControls={() => {}} onExit={onOpen} /></div></div>;
}

function LiveDisplay({ race, matchSeconds, timerSeconds, controlsOpen = false, preview = false, onToggleControls, onExit, onTap, onSubtract, onUndo, onMiss, onResetTimer, onPauseRace, onStartTimer, onPauseTimer, onStartMatchClock, onPauseMatchClock, onResetMatchClock, onFlip, onLandscape }) {
  const landscape = typeof window !== 'undefined' && window.innerWidth > window.innerHeight;
  const playerSides = race.display?.flipHorizontal ? ['PLAYER_B', 'PLAYER_A'] : ['PLAYER_A', 'PLAYER_B'];
  const playerCards = playerSides.map(side => <PlayerCard key={side} race={race} side={side} onTap={onTap} onSubtract={onSubtract} live />);
  return <div className={`arr-live-display ${preview ? 'is-preview' : ''}`}>
    <header className="arr-live-header"><button type="button" onClick={onExit} className="arr-live-icon" aria-label={preview ? 'Open Live View' : 'Exit Live View'}>{preview ? '⛶' : '←'}</button><div><strong>Race Room</strong><small>{label(race.cueMode)} • {label(race.scoringStyle)}</small></div><button type="button" onClick={onToggleControls} className="arr-live-icon" disabled={preview} aria-label="Live View controls">•••</button></header>
    <div className="arr-live-center"><div className="arr-live-clock"><strong>{formatClock(matchSeconds)}</strong><small>Match</small></div><div className="arr-live-format"><span>{label(race.cueMode)}</span><strong>{usesInnings(race) ? 'POINTS + INNINGS' : `RACE TO ${targetFor(race, 'PLAYER_A')}`}</strong><small>{label(pressureState(race))}</small></div><div className={`arr-live-clock ${race.flow?.timerStyle !== 'OFF' && timerSeconds === 0 ? 'is-expired' : ''}`}><strong>{race.flow?.timerStyle === 'OFF' ? '—' : timerSeconds}</strong><small>Shot</small></div></div>
    <main className={`arr-live-players ${landscape ? 'is-landscape' : ''}`}>{playerCards}</main>
    {usesInnings(race) && <div className="arr-live-innings"><span>P1 INN {n(race.inningsA)}</span><span>AVG {averageFor(race, 'PLAYER_A').toFixed(3)}</span><span>HIGH {n(race.highRunA)}</span><b>•</b><span>P2 INN {n(race.inningsB)}</span><span>AVG {averageFor(race, 'PLAYER_B').toFixed(3)}</span><span>HIGH {n(race.highRunB)}</span></div>}
    {!preview && controlsOpen && <div className="arr-live-controls"><button type="button" onClick={onUndo} disabled={!race.undoStack?.length}>Undo</button><button type="button" onClick={onPauseRace}>{race.isPaused ? 'Resume race' : 'Pause race'}</button>{race.flow?.timerStyle !== 'OFF' && <button type="button" onClick={race.flow?.timerRunning ? onPauseTimer : onStartTimer}>{race.flow?.timerRunning ? 'Pause timer' : 'Start timer'}</button>}<button type="button" onClick={onResetTimer} disabled={race.flow?.timerStyle === 'OFF'}>Reset timer</button><button type="button" onClick={race.flow?.matchClockRunning ? onPauseMatchClock : onStartMatchClock}>{race.flow?.matchClockRunning ? 'Pause match clock' : 'Start match clock'}</button><button type="button" onClick={onResetMatchClock}>Reset match clock</button>{usesInnings(race) && <button type="button" onClick={onMiss} disabled={race.activeSide === 'NONE'}>End inning</button>}<button type="button" onClick={onFlip}>{race.display?.flipHorizontal ? 'Default sides' : 'Swap sides'}</button><button type="button" onClick={onLandscape}>{race.display?.lockLandscape ? 'Unlock landscape' : 'Lock landscape'}</button></div>}
    <footer className="arr-live-footer">Tap a player to take the table • tap the active player again to score • hold is reserved for manual table control on Android</footer>
  </div>;
}

function RecentEvents({ race }) {
  const events = [...(race.events || [])].slice(-6).reverse();
  if (!events.length) return null;
  return <section className="arr-panel"><h3>Recent activity</h3><div className="arr-events">{events.map((event, index) => <p key={`${event.createdAt || index}-${event.type}`}><strong>{label(event.type)}</strong><span>{event.scoreA ?? race.scoreA}–{event.scoreB ?? race.scoreB}</span>{event.message && <small>{event.message}</small>}</p>)}</div></section>;
}

function WinnerPanel({ race, onRestart, onDifferent, onNew, onView, compact = false }) {
  const winner = race.winnerName || 'Match complete';
  return <div className={`arr-winner-backdrop ${compact ? 'is-compact' : ''}`} role="dialog" aria-modal="true"><section className="arr-winner-card"><span>FINAL</span><h2>{winner}</h2><p>{race.playerAName} {race.scoreA || 0} – {race.scoreB || 0} {race.playerBName}</p><div className="arr-winner-actions"><button type="button" className="arr-primary" onClick={onRestart}>Rematch</button><button type="button" onClick={onDifferent}>Change setup</button>{!compact && <button type="button" onClick={onView}>Show final</button>}<button type="button" onClick={onNew}>New match</button></div></section></div>;
}

function SessionList({ races, activeId, setActiveId, setTab, history = false }) {
  if (!races.length) return <div className="arr-empty arr-empty-small"><h3>{history ? 'No finished games yet' : 'No other sessions'}</h3><p>{history ? 'Completed Race Room games will appear here.' : 'Open sessions autosave while you play.'}</p></div>;
  return <div className="arr-session-list">{races.map(race => <article className={activeId === race.id ? 'active' : ''} key={race.id}><button type="button" className="arr-session-main" onClick={() => { setActiveId(race.id); setTab('PLAY'); }}><span><strong>{race.playerAName || 'Player 1'} vs {race.playerBName || 'Player 2'}</strong><small>{label(race.cueMode)} • {label(race.status)}</small></span><b>{race.scoreA || 0}–{race.scoreB || 0}</b></button>{!history && <button type="button" className="arr-danger arr-session-delete" onClick={() => window.confirm('Delete this saved session?') && removeRace(race.id)}>Delete</button>}</article>)}</div>;
}
